import { useEffect, useState } from 'react';
import { useGetPrivateAudios, useGetPublicAudios, useSaveAudio } from '../audios';
import { useFetchRequiredCredits } from '../../../t2c-fe/services';
import { useAppContext } from '../context';
import { useDialog } from '../useDialog';
import { useSnackbar } from 'notistack';
import { useRouter } from 'next/router';
import { useAudioCredit, usePopover } from '../text-to-speech';
import {
  getRandomInt,
  isCreditsAvailable,
  MediaStatus,
  AUDIO_DEFAULT_NAME_PREFIX,
  DbModelToDisplayName,
} from '../../../t2c-fe/utils';
import { AudioCredit, Avatar, IAudioCreation } from '../../../t2c-fe/@types';
import { useCreditContext } from '../useCreditContext';
import { useEnhancePrompt } from '../broll/useEnchancePrompt';
import { AudioModelType } from '../../utils';

export const useVoiceover = ({ refetchCreatedAudios }) => {
  const getAudioModelTypeFromAudio = (audioId: string): AudioModelType | undefined => {
    const publicAudio = publicAudioData?.find((audio) => audio.id === audioId);
    const privateAudio = privateAudioData?.pages
      ?.flatMap((page) => page.data)
      ?.find((audio) => audio.id === audioId);
    const selectedAudio = publicAudio || privateAudio;

    if (!selectedAudio?.modelName) return undefined;

    return DbModelToDisplayName[selectedAudio.modelName];
  };
  const { data: privateAudioData } = useGetPrivateAudios({
    filters: { status: MediaStatus.Success },
  });
  const { data: publicAudioData } = useGetPublicAudios();

  const [voiceOverData, setVoiceOverData] = useState({
    text: '',
    name: AUDIO_DEFAULT_NAME_PREFIX + getRandomInt(1, 1000),
    audioId: publicAudioData?.[0]?.id ?? privateAudioData?.pages?.[0]?.data?.[0]?.id,
  });

  const [newGenerationData, setNewGenerationData] = useState<IAudioCreation>();
  const [creditsData, setCreditsData] = useState<AudioCredit | null>(null);
  const { handleUpdateUserCredit } = useCreditContext();

  const {
    showAiGenModel: isProccessingModalOpen,
    handleOpenAiGenModal: openProccessingModal,
    handleCloseAiGenModal: closeProccessingModal,
    showCreditDialog: showCreditDialog,
  } = useAppContext();

  const {
    mutateAsync: mutateFetchRequiredCredits,
    isLoading: isFetchingRequiredCredits,
    data: requiredCreditsData,
    isError: isCreditFetchError,
  } = useFetchRequiredCredits();

  const {
    closeDialog: closeCreditDialog,
    isDialogOpen: isCreditDialogOpen,
    openDialog: openCreditDialog,
  } = useDialog();

  const { mutateAsync: mutateSaveAudio, isLoading } = useSaveAudio();

  const { mutateAsync: mutateEnhancePrompt, isLoading: isEnhancing } = useEnhancePrompt();

  const { enqueueSnackbar } = useSnackbar();
  const handleDataChange = (key: string, value: string) => {
    setVoiceOverData({ ...voiceOverData, [key]: value });
  };

  const handleCreateClick = async () => {
    let errMsg = '';
    if (!voiceOverData.audioId) errMsg = 'Please Select An Audio.';
    if (!voiceOverData.name.trim()) errMsg = 'Please Enter The Name.';
    if (!voiceOverData.text.trim()) errMsg = 'Please Enter A script.';

    if (errMsg) {
      enqueueSnackbar(errMsg, { variant: 'error' });
      return;
    }
    const res = await mutateFetchRequiredCredits({
      text: voiceOverData.text,
      isAudioCreation: true,
      model: getAudioModelTypeFromAudio(voiceOverData.audioId),
    });
    setCreditsData(res);
  };

  useEffect(() => {
    if (creditsData) {
      const isEnoughCredit = isCreditsAvailable(
        creditsData?.availableCredits,
        creditsData?.creditsInUse,
        creditsData?.requiredCredits
      );
      if (showCreditDialog) {
        openCreditDialog();
      } else {
        if (!isFetchingRequiredCredits && isEnoughCredit) {
          handleConfirmGeneration();
        } else if (!isEnoughCredit) {
          // handleUpgrade();
          openCreditDialog();
        }
      }
    }
  }, [creditsData]);

  const handleConfirmGeneration = async () => {
    closeCreditDialog();
    try {
      const sanitizedData = {
        ...voiceOverData,
        name: voiceOverData.name.trim(),
        text: voiceOverData.text.trim(),
      };
      const res = await mutateSaveAudio(sanitizedData);
      setNewGenerationData(res);
      handleUpdateUserCredit();
      openProccessingModal();
      refetchCreatedAudios();
    } catch (error) {
      enqueueSnackbar('Failed to generate', { variant: 'error' });
    }
  };

  const { push } = useRouter();

  const handleUpgrade = () => {
    push('/subscription');
  };
  const { popoverAnchor, handleOpenPopover, handleClosePopover } = usePopover();

  const handleAudioSelect = (id: string) => {
    setVoiceOverData({ ...voiceOverData, audioId: id });
  };

  const handlePromptEnhance = async () => {
    try {
      const res = await mutateEnhancePrompt({
        prompt: voiceOverData.text,
        model: 'voiceover',
      });
      handleDataChange('text', res);
    } catch (error) {
      enqueueSnackbar('Failed to enchance prompt', { variant: 'error' });
    }
  };

  return {
    voiceOverData,
    handleDataChange,
    handleCreateClick,
    isCreditDialogOpen,
    closeCreditDialog,
    isFetchingRequiredCredits,
    requiredCreditsData,
    isProccessingModalOpen,
    closeProccessingModal,
    handleUpgrade,
    handleConfirmGeneration,
    privateAudioData,
    publicAudioData,
    popoverAnchor,
    handleOpenPopover,
    handleClosePopover,
    handleAudioSelect,
    isLoading,
    newGenerationData,
    isEnhancing,
    handlePromptEnhance,
  };
};
